import { useEffect } from "react";

export const useGameActionListeners = (
  socket,
  name,
  {
    setPlayerActionCards,
    setNewActionCardIds,
    setSelectedActionCard,
    setActionMode,
    showMessage,
    addNotification,
    setLoggedIn
  }
) => {
  useEffect(() => {
    const currentSocket = socket.current;
    if (!currentSocket) return;

    const handleActionCardsUpdate = ({ playerName, actionCards }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] action_cards_update received', { playerName });
      }
      if (playerName === name) {
        setPlayerActionCards(prev => {
          const prevIds = new Set(prev.map(card => card.id));
          const newCards = Array.isArray(actionCards) ? actionCards : [];
          
          const newlyAddedIds = newCards
            .filter(card => card.id && !prevIds.has(card.id))
            .map(card => card.id);
          
          if (newlyAddedIds.length > 0) {
            setNewActionCardIds(prevIds => {
              const updated = new Set(prevIds);
              newlyAddedIds.forEach(id => updated.add(id));
              return updated;
            });
          }
          
          return newCards;
        });
      }
    };

    const handleActionResult = ({ success, message, data }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] action_result received', { success });
      }
      if (success) {
        if (data?.event) {
          showMessage(message);
        } else {
          addNotification(`✅ ${message}`, null);
        }
        
        if (setSelectedActionCard) setSelectedActionCard(null);
        if (setActionMode) setActionMode(null);
      } else {
        if (message && message.includes("Room not found")) {
          showMessage("❌ Connection to room lost. Returning to home page...", () => {
            setLoggedIn(false);
          });
        } else {
          showMessage(`❌ ${message}`);
        }
      }
    };

    const handleDrawActionResult = ({ success, message, card }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] draw_action_result received', { success, card });
      }
      if (success) {
        // Show notification with the card that was drawn
        const cardName = card?.name || message?.replace('Drew ', '') || 'an action card';
        addNotification(`🃏 You drew: ${cardName}`, null);
      } else {
        if (message && message.includes("Room not found")) {
          showMessage("❌ Connection to room lost. Returning to home page...", () => {
            setLoggedIn(false);
          });
        } else {
          showMessage(`❌ ${message}`);
        }
      }
    };

    const handleDiscardRedrawResult = ({ success, message }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] discard_redraw_result received', { success });
      }
      if (success) {
        addNotification(`🔄 ${message}`, null);
      } else {
        if (message && message.includes("Room not found")) {
          showMessage("❌ Connection to room lost. Returning to home page...", () => {
            setLoggedIn(false);
          });
        } else {
          showMessage(`❌ ${message}`);
        }
      }
    };

    currentSocket.on("action_cards_update", handleActionCardsUpdate);
    currentSocket.on("action_result", handleActionResult);
    currentSocket.on("draw_action_result", handleDrawActionResult);
    currentSocket.on("discard_redraw_result", handleDiscardRedrawResult);

    return () => {
      currentSocket.off("action_cards_update", handleActionCardsUpdate);
      currentSocket.off("action_result", handleActionResult);
      currentSocket.off("draw_action_result", handleDrawActionResult);
      currentSocket.off("discard_redraw_result", handleDiscardRedrawResult);
    };
  }, [socket, name, setPlayerActionCards, setNewActionCardIds, setSelectedActionCard, setActionMode, showMessage, addNotification, setLoggedIn]);
};

